import { type ClientError } from 'graphql-request';
import * as React from 'react';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';

import { PATHS, AUTH_TOKEN_KEY, AUTH_COOKIE_DOMAIN } from '@/constants';
import { type AuthMutation, type AuthMutationVariables, useAuthMutation, useMeQuery } from '@/graphql';

import { AnonymousGuardContext, type AnonymousGuardContextType } from './context';

export const AnonymousGuardProvider: React.FC<{ children: JSX.Element | null }> = ({ children }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies([AUTH_TOKEN_KEY]);
  const { data } = useMeQuery(
    {},
    {
      onError: () => {
        removeCookie(AUTH_TOKEN_KEY, { domain: AUTH_COOKIE_DOMAIN });
      },
      enabled: Boolean(cookies[AUTH_TOKEN_KEY]),
    }
  );

  const { mutate: createAuthToken, ...createAuthTokenResult } = useAuthMutation<ClientError>({
    onSuccess: (data: AuthMutation) => {
      setCookie(AUTH_TOKEN_KEY, data.auth, { domain: AUTH_COOKIE_DOMAIN });
      navigate(PATHS.SCORING_DASHBOARD);
    },
  });

  const authenticateFunction = async (data: Omit<AuthMutationVariables, 'recaptchaToken'>): Promise<void> => {
    // @ts-expect-error executeRecaptcha exists
    const recaptchaToken = await executeRecaptcha('scoring_app_login');

    createAuthToken({
      ...data,
      recaptchaToken,
    });
  };

  const contextValue: AnonymousGuardContextType = {
    authenticate: (data: Omit<AuthMutationVariables, 'recaptchaToken'>) => {
      void (async (data) => {
        await authenticateFunction(data);
      })(data);
    },
    authenticateResult: createAuthTokenResult,
  };

  useEffect(() => {
    if (data) {
      navigate(PATHS.SCORING_DASHBOARD);
    }
  }, [data, navigate]);

  return <AnonymousGuardContext.Provider value={contextValue}>{children}</AnonymousGuardContext.Provider>;
};
